/**
 * 초대(추천) 링크 처리. 공유 링크는 https://.../?ref=<코드> 형태다.
 * 방문 시 코드를 저장해 뒀다가 온보딩 때 서버로 보내 추천인에게 100토마토가 가게 한다.
 */
const REF_KEY = 'tomato_ref';

/** URL에 ?ref=가 있으면 저장하고 주소창에서 지운다(새로고침·공유 시 지저분하지 않게). */
export function captureRefFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref && /^[A-Z0-9]{4,16}$/i.test(ref)) {
      localStorage.setItem(REF_KEY, ref.toUpperCase());
      params.delete('ref');
      const qs = params.toString();
      const url = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState(null, '', url);
    }
  } catch {
    /* localStorage/URL 접근 실패는 무시 — 초대 적립만 안 될 뿐이다. */
  }
}

export function getStoredRef(): string | null {
  try {
    return localStorage.getItem(REF_KEY);
  } catch {
    return null;
  }
}

export function clearStoredRef(): void {
  try {
    localStorage.removeItem(REF_KEY);
  } catch {
    /* 무시 */
  }
}

/** 내 초대 링크. */
export function buildInviteUrl(referralCode: string): string {
  return `${window.location.origin}/?ref=${referralCode}`;
}

/**
 * 초대 코드로 초대자의 활동명을 물어본다("○○님이 초대했어요" 표시용).
 * 서버가 활동명만 주고, 유효하지 않거나 탈퇴·온보딩 미완이면 null을 준다.
 * 실패는 조용히 null — 화면엔 초대 문구만 안 뜰 뿐이다.
 */
export async function fetchInviterNickname(code: string): Promise<string | null> {
  try {
    const res = await fetch(`/api/invite/${encodeURIComponent(code)}`, {
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { nickname?: string | null };
    return data.nickname ?? null;
  } catch {
    return null;
  }
}

const INVITE_TEXT =
  '🍅 토마토 마켓 — 헥토 사내 벼룩시장이에요. 안 쓰는 물건, 필요한 동료에게. 회사 메일로 바로 들어와요.';

/**
 * 초대 공유. 모바일은 OS 공유 시트(카톡 등)를 띄우고, 안 되면 클립보드 복사로 떨어진다.
 * 반환값: 'shared' | 'copied' | 'failed'.
 */
export async function shareInvite(url: string): Promise<'shared' | 'copied' | 'failed'> {
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: '토마토 마켓', text: INVITE_TEXT, url });
      return 'shared';
    } catch (err) {
      // 사용자가 공유 시트를 닫은 것뿐이면 실패로 보지 않는다.
      if (err instanceof Error && err.name === 'AbortError') return 'shared';
      /* 그 외에는 복사로 폴백 */
    }
  }
  try {
    await navigator.clipboard.writeText(`${INVITE_TEXT}\n${url}`);
    return 'copied';
  } catch {
    return 'failed';
  }
}
